"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { useInvoiceStore } from "@/lib/invoice/store";
import { LETTERHEADS, LETTER_STARTERS } from "@/lib/invoice/letters";
import { fmtDate } from "@/lib/invoice/utils";
import LetterPreview from "@/components/Invoice/LetterPreview";
import { Button, Card, Empty, Input, Loading, PageHeader, Select, cx, useFeedback } from "@/components/Invoice/ui";

export default function LetterheadPage() {
  const store = useInvoiceStore();
  const router = useRouter();
  const { toast, confirm } = useFeedback();
  const [brandId, setBrandId] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");

  const brand = store.ready ? store.brandById(brandId || store.settings.defaultBrandId) : null;
  const letters = useMemo(
    () => [...(store.letters || [])].sort((a, b) => String(b.date).localeCompare(String(a.date)) || String(b.number).localeCompare(String(a.number))),
    [store.letters]
  );

  if (!store.ready) return <Loading />;

  const current = brand?.letterhead || "classic";
  const shown = letters.filter((l) => [l.number, l.subject, l.to?.name, l.body].join(" ").toLowerCase().includes(q.toLowerCase()));
  const dateStyle = store.settings.defaults.dateStyle === "iso" ? "pretty" : store.settings.defaults.dateStyle;

  const blankPdf = async (design) => {
    setBusy(design);
    try {
      const { downloadLetterPdf } = await import("@/lib/invoice/letterPdf");
      await downloadLetterPdf({ design }, brand, { blank: true });
    } finally {
      setBusy("");
    }
  };
  const letterPdf = async (l) => {
    setBusy(l.id);
    try {
      const { downloadLetterPdf } = await import("@/lib/invoice/letterPdf");
      await downloadLetterPdf(l, store.brandById(l.brandId), { dateStyle });
    } finally {
      setBusy("");
    }
  };
  const makeDefault = (design) => {
    store.saveBrand({ ...brand, letterhead: design });
    toast(`${LETTERHEADS[design].label} is now the letterhead for ${brand.name}`, { tone: "success" });
  };
  const newLetter = (params = {}) => {
    const sp = new URLSearchParams({ ...(brandId ? { brand: brandId } : {}), ...params });
    router.push(`/invoice/letterhead/edit${sp.toString() ? `?${sp}` : ""}`);
  };

  return (
    <div>
      <PageHeader
        eyebrow="Documents"
        title="Letterhead"
        subtitle="Write letters on your own branded letterhead – cover letters, reminders, certificates and more – or download a blank letterhead to print. Logo, colour, address and IDs come from your business profile."
        actions={
          <>
            {store.brands.length > 1 && (
              <Select className="max-w-[220px]" value={brandId || store.settings.defaultBrandId} onChange={(e) => setBrandId(e.target.value)}>
                {store.brands.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </Select>
            )}
            <Link href="/invoice/settings">
              <Button variant="secondary">Edit logo &amp; details</Button>
            </Link>
            <Button onClick={() => newLetter()}>+ New letter</Button>
          </>
        }
      />

      <Card title="Letterhead designs" subtitle={`Shown with ${brand?.name || "your business"}’s logo and colour · ★ = default for this business`} className="mb-6">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {Object.entries(LETTERHEADS).map(([key, t]) => (
            <div key={key} className={cx("overflow-hidden rounded-xl border-2 bg-white transition", current === key ? "border-brand-600 ring-2 ring-brand-100" : "border-slate-200 hover:border-slate-300")} data-testid={`letterhead-${key}`}>
              <button type="button" className="relative block h-[230px] w-full overflow-hidden bg-slate-100" onClick={() => newLetter({ design: key })} title="Write a letter with this design">
                <div className="pointer-events-none absolute left-1/2 top-2 origin-top -translate-x-1/2 scale-[0.25] shadow">
                  <LetterPreview letter={null} brand={brand} design={key} thumbnail />
                </div>
              </button>
              <div className="border-t border-slate-100 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-slate-800">
                    {current === key && <span className="text-amber-500">★ </span>}
                    {t.label}
                  </div>
                  <span className={cx("rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide", t.tier === "premium" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500")}>{t.tier}</span>
                </div>
                <div className="truncate text-[11px] text-slate-500">{t.desc}</div>
                <div className="mt-2 flex flex-wrap gap-1">
                  <Button size="sm" onClick={() => newLetter({ design: key })}>Write</Button>
                  <Button size="sm" variant="secondary" disabled={busy === key} onClick={() => blankPdf(key)}>
                    {busy === key ? "…" : "Blank PDF"}
                  </Button>
                  {current !== key && (
                    <Button size="sm" variant="ghost" onClick={() => makeDefault(key)} title="Use this design by default">
                      ☆
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Start from a ready-made letter" subtitle="Text with blanks to fill in – client and document details are filled for you" className="mb-6">
        <div className="flex flex-wrap gap-2">
          {LETTER_STARTERS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => newLetter({ starter: s.id })}
              className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-sm text-slate-700 transition hover:border-brand-300 hover:bg-brand-50 hover:text-brand-800"
            >
              <span>{s.icon}</span>
              {s.label}
            </button>
          ))}
        </div>
      </Card>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-[15px] font-semibold text-slate-900">Your letters ({letters.length})</div>
        <Input className="max-w-xs" placeholder="Search letters…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {shown.length === 0 ? (
        <Empty title={letters.length ? "No letters match" : "No letters yet"}>{letters.length ? "Try another search." : "Pick a design or a ready-made letter above to write your first one."}</Empty>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200/80 bg-white shadow-card">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Ref</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">To</th>
                <th className="px-4 py-3">Subject</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {shown.map((l) => (
                <tr key={l.id} className="hover:bg-slate-50/60">
                  <td className="px-4 py-3">
                    <Link className="font-semibold text-slate-900 hover:text-brand-700" href={`/invoice/letterhead/edit?id=${l.id}`}>
                      {l.number || "—"}
                    </Link>
                    <div className="text-[11px] text-slate-400">{LETTERHEADS[l.design]?.label}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(l.date, "pretty")}</td>
                  <td className="px-4 py-3">{l.to?.name || "—"}</td>
                  <td className="max-w-[320px] truncate px-4 py-3 text-slate-700">{l.subject || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <div className="flex justify-end gap-1.5">
                      <Button size="sm" variant="secondary" onClick={() => router.push(`/invoice/letterhead/edit?id=${l.id}`)}>Open</Button>
                      <Button size="sm" variant="secondary" disabled={busy === l.id} onClick={() => letterPdf(l)}>{busy === l.id ? "…" : "PDF"}</Button>
                      <Button size="sm" variant="ghost" onClick={() => router.push(`/invoice/letterhead/edit?duplicate=${l.id}`)}>Duplicate</Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          if (!(await confirm(`Delete letter ${l.number || ""}? It goes to the recycle bin for 30 days.`, { okLabel: "Delete", danger: true }))) return;
                          store.deleteLetter(l.id);
                          toast("Letter moved to the recycle bin");
                        }}
                      >
                        ✕
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
